import {
  Address,
  CompanyEntity,
  CompanyUnitEntity,
  FreightTypeEntity,
  GeofenceSiteGroupEntity,
  getObjectId,
  OperatingAreaEntity,
  SiteEntity,
  SiteType,
  toSiteType
} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, ModalProps, NumberInput, Switch, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {SiteTypeSelect} from "../../select/entity/SiteTypeSelect/SiteTypeSelect";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {CompanyUnitSelect} from "../../select/entity/CompanyUnitSelect/CompanyUnitSelect";
import {OperatingAreaSelect} from "../../select/entity/OperatingAreaSelect/OperatingAreaSelect";
import {useSiteForm} from "./useSiteForm";
import {SiteSelect} from "../../select/entity/SiteSelect/SiteSelect";
import {GeofenceSiteGroupSelect} from "../../select/entity/GeofenceSiteGroupSelect/GeofenceSiteGroupSelect";
import {AddressFormFields} from "../AddressForm/AddressForm";

export interface SiteFormProps {
  options?: SiteFormOptions;
  defaultValues?: Partial<SiteFormValues>;
  onSubmit?: (values: SiteFormValues) => void;
  submitting?: boolean;
}

export interface SiteFormOptions {
  // props here
}

export interface SiteFormValues {
  name: string
  leaseId?: string
  leaseName?: string
  type: SiteType
  freightType?: FreightTypeEntity | string;
  operator: CompanyEntity | string;
  owner?: CompanyEntity | string;
  operatingArea?: OperatingAreaEntity | string;
  companyUnit?: CompanyUnitEntity | string;
  parentSite?: SiteEntity | string;
  geofenceSiteGroup?: GeofenceSiteGroupEntity | string;
  afe?: string
  afePrefix?: string
  afeSuffix?: string
  address?: Address
  latCoord?: number
  longCoord?: number
  transportViaPipeline?: boolean
  transportOilViaPipeline?: boolean
  doNotUse?: boolean
}

export interface SiteFormDefaultValues extends Partial<SiteFormValues> {

}

export interface SiteFormRawValues {
  name?: string
  leaseId?: string
  leaseName?: string
  type?: string
  freightType?: string;
  operator?: string;
  owner?: string;
  operatingArea?: string;
  companyUnit?: string;
  parentSite?: string;
  geofenceSiteGroup?: string;
  afe?: string
  afePrefix?: string
  afeSuffix?: string
  address?: Address
  latCoord?: number
  longCoord?: number
  transportViaPipeline?: boolean
  transportOilViaPipeline?: boolean
  doNotUse?: boolean
}

export type SiteFormType = UseFormReturnType<SiteFormRawValues, (values: SiteFormRawValues) => SiteFormRawValues>

export const SiteForm = (props: SiteFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {regionId} = useApp();

  const {t} = useTranslation(["common"]);

  const form = useSiteForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        name: values.name!,
        leaseId: values.leaseId,
        leaseName: values.leaseName,
        type: values.type ? toSiteType(values.type) : undefined,
        freightType: values.freightType,
        operator: values.operator!,
        owner: values.owner,
        operatingArea: values.operatingArea,
        companyUnit: values.companyUnit,
        parentSite: values.parentSite,
        geofenceSiteGroup: values.geofenceSiteGroup,
        afe: values.afe,
        afePrefix: values?.afePrefix,
        afeSuffix: values?.afeSuffix,
        address: values.address,
        latCoord: values.latCoord,
        longCoord: values.longCoord,
        transportViaPipeline: values?.transportViaPipeline,
        transportOilViaPipeline: values?.transportOilViaPipeline,
        doNotUse: values.doNotUse,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <TextInput
            size="xs"
            label={t("common.entity.site.form.field.name", "Name")}
            placeholder={t<string>("common.entity.site.form.field.name.placeholder", "Name")}
            withAsterisk
            required
            {...form.getInputProps("name")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <SiteTypeSelect
            size="xs"
            label={t("common.entity.site.form.field.type", "Type")}
            placeholder={t<string>("common.entity.site.form.field.type.placeholder", "Type")}
            clearable
            {...form.getInputProps("type")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <FreightTypeSelect
            size="xs"
            label={t("common.entity.site.form.field.freighttype", "Default Freight Type")}
            placeholder={t<string>("common.entity.site.form.field.freighttype.placeholder", "Default Freight Type")}
            selector={{
              regionId: regionId,
            }}
            {...form.getInputProps("freightType")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <CompanySelect
            size="xs"
            label={t("common.entity.site.form.field.operator", "Operator")}
            placeholder={t<string>("common.entity.site.form.field.operator.placeholder", "Operator")}
            withAsterisk={true}
            required={true}
            selector={{
              regionId: regionId,
            }}
            {...form.getInputProps("operator")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <CompanySelect
            size="xs"
            label={t("common.entity.site.form.field.owner", "Owner")}
            placeholder={t<string>("common.entity.site.form.field.owner.placeholder", "Owner")}
            selector={{
              regionId: regionId,
            }}
            {...form.getInputProps("owner")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <CompanyUnitSelect
            size="xs"
            label={t("common.entity.site.form.field.companyunit", "Company Unit")}
            placeholder={t<string>("common.entity.site.form.field.companyunit.placeholder", "Company Unit")}
            selector={{
              companyId: getObjectId(form.getTransformedValues().operator),
              regionId: regionId,
            }}
            {...form.getInputProps("companyUnit")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <SiteSelect
            size="xs"
            label={t("common.entity.site.form.field.parentsite", "Parent Site")}
            placeholder={t<string>("common.entity.site.form.field.parentsite.placeholder", "Parent Site")}
            selector={{
              company: getObjectId(form.getTransformedValues().operator),
              regionId: regionId,
            }}
            {...form.getInputProps("parentSite")} />
        </Grid.Col>
        <Grid.Col span={6}>
          {/*empty*/}
        </Grid.Col>
        <Grid.Col span={6}>
          <OperatingAreaSelect
            size="xs"
            label={t("common.entity.site.form.field.operatingarea", "Operating Area")}
            placeholder={t<string>("common.entity.site.form.field.operatingarea.placeholder", "Operating Area")}
            selector={{
              companyId: getObjectId(form.getTransformedValues().operator),
              regionId: regionId,
            }}
            {...form.getInputProps("operatingArea")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            size="xs"
            label={t("common.entity.site.form.field.leaseid", "Lease ID")}
            placeholder={t<string>("common.entity.site.form.field.leaseid.placeholder", "Lease ID")}
            {...form.getInputProps("leaseId")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            size="xs"
            label={t("common.entity.site.form.field.leasename", "Lease Name")}
            placeholder={t<string>("common.entity.site.form.field.leasename.placeholder", "Lease Name")}
            {...form.getInputProps("leaseName")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <AddressFormFields
        grow
        gutter="xs"
        form={form}
        path={"address"}/>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <NumberInput
            size="xs"
            label={t("common.entity.site.form.field.lat", "Latitude")}
            placeholder={t<string>("common.entity.site.form.field.lat.placeholder", "Latitude")}
            precision={6}
            min={-90}
            max={90}
            step={0.000001}
            hideControls
            {...form.getInputProps("latCoord")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput
            size="xs"
            label={t("common.entity.site.form.field.long", "Longitude")}
            placeholder={t<string>("common.entity.site.form.field.long.placeholder", "Longitude")}
            precision={6}
            min={-180}
            max={180}
            step={0.000001}
            hideControls
            {...form.getInputProps("longCoord")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <GeofenceSiteGroupSelect
            size="xs"
            label={t("common.entity.site.form.field.geofencesitegroup", "Geofence Site Group")}
            placeholder={t<string>("common.entity.site.form.field.geofencesitegroup.placeholder", "Geofence Site Group")}
            selector={{
              regionId: regionId,
            }}
            {...form.getInputProps("geofenceSiteGroup")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.site.form.field.afeprefix", "AFE Prefix")}
            placeholder={t<string>("common.entity.site.form.field.afeprefix.placeholder", "AFE Prefix")}
            {...form.getInputProps("afePrefix")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.site.form.field.afe", "AFE")}
            placeholder={t<string>("common.entity.site.form.field.afe.placeholder", "AFE")}
            {...form.getInputProps("afe")} />
        </Grid.Col>
        <Grid.Col span={4}>
          <TextInput
            size="xs"
            label={t("common.entity.site.form.field.afesuffix", "AFE Suffix")}
            placeholder={t<string>("common.entity.site.form.field.afesuffix.placeholder", "AFE Suffix")}
            {...form.getInputProps("afeSuffix")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <Switch
            size="xs"
            label={t("common.entity.site.form.field.transportviapipeline", "Transport Water Via Pipeline")}
            placeholder={t<string>("common.entity.site.form.field.transportviapipeline", "Transport Water Via Pipeline")}
            {...form.getInputProps("transportViaPipeline", {type: 'checkbox'})} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Switch
            size="xs"
            label={t("common.entity.site.form.field.transportviapipeline", "Transport Oil Via Pipeline")}
            placeholder={t<string>("common.entity.site.form.field.transportviapipeline", "Transport Oil Via Pipeline")}
            {...form.getInputProps("transportOilViaPipeline", {type: 'checkbox'})} />
        </Grid.Col>
        <Grid.Col span={12}>
          <Switch
            size="xs"
            label={t("common.entity.site.form.field.donotuse", "Do Not Use")}
            placeholder={t<string>("common.entity.site.form.field.donotuse", "Do Not Use")}
            {...form.getInputProps("doNotUse", {type: 'checkbox'})} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface SiteFormModalProps extends SiteFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const SiteFormModal = (props: SiteFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <SiteForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
